import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { Kind, type OperationDefinitionNode, visit } from "graphql";

import {
  ListEffectiveCategoryPropertySchemasDocument,
  type ListEffectiveCategoryPropertySchemasQuery,
} from "~/features/admin/categories/gqls";

type EffectiveResponse = ListEffectiveCategoryPropertySchemasQuery["cnutListEffectiveCategoryPropertySchemas"];
type EffectiveOutput = Extract<EffectiveResponse, { __typename: "ListEffectiveCategoryPropertySchemasOutput" }>;
type EffectiveRecordPropertySchema = Pick<
  EffectiveOutput["schemas"][number],
  "metadataKey" | "type" | "position" | "selectOptions"
>;
export type EffectiveRecordPropertySchemasByCategory = ReadonlyMap<string, readonly EffectiveRecordPropertySchema[]>;
type EffectiveRecordSchemasQuery = Record<string, EffectiveResponse>;

// One query keeps the number of React hooks stable when a Record's assigned categories change.
export function buildEffectiveRecordSchemasQuery(categoryIDs: readonly string[]) {
  const operation = ListEffectiveCategoryPropertySchemasDocument.definitions.find(
    (definition): definition is OperationDefinitionNode => definition.kind === Kind.OPERATION_DEFINITION,
  );
  if (!operation) throw new Error("Effective category schema query is missing its operation");
  const tenant = operation.variableDefinitions?.find((definition) => definition.variable.name.value === "tenantId");
  const category = operation.variableDefinitions?.find((definition) => definition.variable.name.value === "categoryId");
  const field = operation.selectionSet.selections[0];
  if (!tenant || !category || field?.kind !== Kind.FIELD) throw new Error("Unexpected effective category schema query");
  const renameCategoryVariable = (index: number) => ({
    Variable(node: { kind: typeof Kind.VARIABLE; name: { kind: typeof Kind.NAME; value: string } }) {
      return node.name.value === "categoryId"
        ? { ...node, name: { ...node.name, value: `categoryId${index}` } }
        : undefined;
    },
  });
  const query: TypedDocumentNode<EffectiveRecordSchemasQuery, Record<string, string>> = {
    kind: Kind.DOCUMENT,
    definitions: [
      {
        ...operation,
        name: { kind: Kind.NAME, value: "EffectiveRecordCategoryPropertySchemas" },
        variableDefinitions:
          categoryIDs.length > 0
            ? [tenant, ...categoryIDs.map((_, index) => visit(category, renameCategoryVariable(index)))]
            : [],
        selectionSet: {
          kind: Kind.SELECTION_SET,
          selections:
            categoryIDs.length > 0
              ? categoryIDs.map((_, index) => ({
                  ...visit(field, renameCategoryVariable(index)),
                  alias: { kind: Kind.NAME, value: `category${index}` },
                }))
              : [{ kind: Kind.FIELD, name: { kind: Kind.NAME, value: "__typename" } }],
        },
      },
    ],
  };
  return query;
}

export function readEffectiveRecordSchemas(
  categoryIDs: readonly string[],
  data: EffectiveRecordSchemasQuery,
): EffectiveRecordPropertySchemasByCategory {
  return new Map(
    categoryIDs.map((id, index) => {
      const response = data[`category${index}`];
      if (response?.__typename !== "ListEffectiveCategoryPropertySchemasOutput") {
        throw new Error(`Could not resolve inherited property schemas for category ${id}`);
      }
      return [id, response.schemas] as const;
    }),
  );
}

export function requireEffectiveRecordSchemas(schemas: EffectiveRecordPropertySchemasByCategory, categoryID: string) {
  const result = schemas.get(categoryID);
  if (!result) throw new Error(`Effective property schemas are missing for category ${categoryID}`);
  return result;
}
